import { createMemoryHistory, createRouter, createWebHistory } from 'vue-router'
import { type RouteLocationNormalized, type RouterOptions } from 'vue-router'
import HomeView from '../views/home/HomeView.vue'
import Config from '@/utils/config'
import { cases } from '@/views/cases/cases'
import { articleKeys } from '@/views/blog/articles'

// Unknown slugs render the 404 page under the requested URL instead of
// silently falling back to the first entry, so the server can answer 404.
const notFound = (to: RouteLocationNormalized) => ({
  name: 'notFound',
  params: { pathMatch: to.path.substring(1).split('/') },
  query: to.query,
  hash: to.hash,
})

// One router per app instance. On the server every request renders its own
// app; a shared router would carry the previous request's route into the next
// one (and skip beforeEnter guards when only the params change).
export const createAppRouter = () => createRouter({
  history: Config.ssr
    ? createMemoryHistory(import.meta.env.BASE_URL)
    : createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: `/:locale(${Config.languages.join('|')})?`,
      children: [
        {
          path: '',
          name: 'home',
          component: HomeView
        },
        {
          path: 'services',
          name: 'services',
          component: () => import('../views/services/ServicesView.vue')
        },
        {
          path: 'workbench',
          name: 'workbench',
          component: () => import('../views/workbench/WorkbenchView.vue')
        },
        {
          path: 'approach',
          name: 'approach',
          component: () => import('../views/approach/ApproachView.vue')
        },
        {
          path: 'cases',
          name: 'cases',
          component: () => import('../views/cases/CasesView.vue')
        },
        {
          path: 'cases/:slug',
          name: 'caseDetail',
          component: () => import('../views/cases/detail/CaseDetailView.vue'),
          beforeEnter: (to) =>
            cases.some((c) => c.key === to.params.slug) || notFound(to),
        },
        {
          path: 'blog',
          name: 'blog',
          component: () => import('../views/blog/BlogView.vue')
        },
        {
          path: 'blog/:slug',
          name: 'blogDetail',
          component: () => import('../views/blog/detail/BlogDetailView.vue'),
          beforeEnter: (to) =>
            articleKeys.includes(String(to.params.slug)) || notFound(to),
        },
        {
          path: 'about',
          name: 'about',
          component: () => import('../views/about/AboutView.vue')
        },
        {
          path: 'contact',
          name: 'contact',
          component: () => import('../views/contact/ContactView.vue')
        },
        {
          path: 'book-meeting',
          name: 'bookMeeting',
          component: () => import('../views/BookMeetingView.vue')
        },
        {
          path: 'support',
          name: 'support',
          component: () => import('../views/support/SupportView.vue')
        },
        {
          path: 'imprint',
          name: 'imprint',
          component: () => import('../views/ImprintView.vue')
        },
        {
          path: 'privacy',
          name: 'privacy',
          component: () => import('../views/PrivacyView.vue')
        },
        {
          path: 'terms',
          name: 'terms',
          component: () => import('../views/TermsView.vue')
        },
      ],
    },
    {
      // Internal studio for generating LinkedIn / social post visuals, the
      // company profile and the industry case study PDFs. Rendered
      // chrome-free (bare) and never server-rendered or indexed.
      path: '/studio',
      meta: { bare: true, ssr: false },
      children: [
        {
          path: '',
          name: 'studio',
          component: () => import('../views/studio/StudioView.vue')
        },
        {
          path: 'cards',
          name: 'studioCards',
          component: () => import('../views/studio/cards/CardsView.vue')
        },
        {
          path: 'cards/:card',
          name: 'studioCard',
          component: () => import('../views/studio/cards/CardView.vue')
        },
        {
          path: 'ads',
          name: 'studioAds',
          component: () => import('../views/studio/ads/AdsView.vue')
        },
        {
          path: 'ads/:ad',
          name: 'studioAd',
          component: () => import('../views/studio/ads/AdView.vue')
        },
        {
          path: 'cases',
          name: 'studioCases',
          component: () => import('../views/studio/cases/CasesView.vue')
        },
        {
          path: 'cases/:case',
          name: 'studioCase',
          component: () => import('../views/studio/cases/CaseView.vue')
        },
        {
          path: 'profile',
          name: 'studioProfile',
          component: () => import('../views/studio/profile/ProfileView.vue')
        },
        {
          path: 'profile/print',
          name: 'studioProfilePrint',
          component: () => import('../views/studio/profile/ProfilePrintView.vue')
        },
        {
          path: 'profile/:page',
          name: 'studioProfilePage',
          component: () => import('../views/studio/profile/ProfilePageView.vue')
        },
      ],
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'notFound',
      component: () => import('../views/NotFoundView.vue')
    },
  ],
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    if (to.hash) {
      // Offset by the sticky header so the target's heading stays visible.
      return {
        el: to.hash,
        top: 88,
        behavior: from.path === to.path ? 'smooth' : 'auto'
      }
    }
    return {
      top: 0,
      left: 0
    }
  },
} as RouterOptions)
